import React, { createContext, useContext, useState, useEffect } from 'react';

export interface Workspace {
  workspaceId:   string;
  name:          string;
  type:          'demo' | 'live';
  isReadOnly:    boolean;
  twinMakerWsId: string;
  status:        string;
}

interface WorkspaceCtx {
  workspace:       Workspace | null;
  workspaces:      Workspace[];
  switchWorkspace: (id: string) => void;
  isDemo:          boolean;
  guardWrite:      (action: string) => boolean;
}

const WorkspaceContext = createContext<WorkspaceCtx>({} as WorkspaceCtx);

const DEMO_WORKSPACE: Workspace = {
  workspaceId:   'demo',
  name:          'CookieFactory Demo',
  type:          'demo',
  isReadOnly:    true,
  twinMakerWsId: 'CookieFactory',
  status:        'active',
};

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([DEMO_WORKSPACE]);
  const [workspace,  setWorkspace]  = useState<Workspace>(DEMO_WORKSPACE);

  useEffect(() => {
    const token = localStorage.getItem('cf_token');

    // If logged in via Cognito, fetch real workspaces
    if (token) {
      fetch(`${API}/workspaces`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [DEMO_WORKSPACE])
        .then((data: Workspace[]) => {
          const all = data.length ? data : [DEMO_WORKSPACE];
          setWorkspaces(all);
          const saved = localStorage.getItem('cf_workspace');
          setWorkspace(all.find(w => w.workspaceId === saved) || all[0]);
        })
        .catch(() => {
          setWorkspaces([DEMO_WORKSPACE]);
          setWorkspace(DEMO_WORKSPACE);
        });
    } else {
      // Not logged in via Cognito — use demo only
      const saved = localStorage.getItem('cf_workspace') || 'demo';
      setWorkspace(saved === 'demo' ? DEMO_WORKSPACE : DEMO_WORKSPACE);
    }
  }, []);

  const switchWorkspace = (id: string) => {
    const ws = workspaces.find(w => w.workspaceId === id);
    if (!ws) return;
    setWorkspace(ws);
    localStorage.setItem('cf_workspace', id);
  };

  const guardWrite = (action: string): boolean => {
    if (workspace?.isReadOnly) {
      alert(`"${action}" is disabled in demo mode.\nCreate your own workspace to make changes.`);
      return false;
    }
    return true;
  };

  return (
    <WorkspaceContext.Provider value={{
      workspace, workspaces, switchWorkspace,
      isDemo: workspace?.type === 'demo',
      guardWrite,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => useContext(WorkspaceContext);
